import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useSession } from '@/client/lib/auth'
import { useHomeStats, type HomeStats } from '@/client/modules/dashboard/hooks/useHomeStats'
import { formatDistanceToNow } from 'date-fns'
import {
  Package,
  Search,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Zap,
  Tag,
  Users,
  Loader2,
} from 'lucide-react'

/**
 * Birch Creative Search — owner home.
 *
 * Philosophy: answer four questions at a glance —
 *   1. Is the catalogue healthy? (product count, sync freshness, AI enrichment)
 *   2. What's demand doing? (today vs yesterday, this week vs last)
 *   3. Where are the gaps? (zero-results, struggling queries)
 *   4. What's trending? (top searches, top clicked products)
 *
 * Deep dives live on /dashboard/insights.
 */
export function DashboardPage() {
  const { data: session } = useSession()
  const { data: stats, isLoading, error } = useHomeStats()
  const firstName = session?.user?.name?.split(' ')[0]

  return (
    <div className="space-y-8 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold">
          {firstName ? `G'day, ${firstName}` : 'Welcome'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Birch Creative Search — state of the catalogue, demand, and anything that needs your attention.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Failed to load dashboard. Please refresh.</span>
        </div>
      )}

      <CatalogueSection stats={stats} isLoading={isLoading} />
      <DemandSection stats={stats} isLoading={isLoading} />
      <AttentionSection stats={stats} isLoading={isLoading} />
      <TrendingSection stats={stats} isLoading={isLoading} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Sections
// ─────────────────────────────────────────────

function CatalogueSection({ stats, isLoading }: { stats?: HomeStats; isLoading: boolean }) {
  const queryClient = useQueryClient()
  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!response.ok) throw new Error(await response.text())
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-stats'] })
    },
  })

  const sync = stats?.sync
  const lastSync = sync?.lastSyncAt
    ? formatDistanceToNow(sync.lastSyncAt * 1000, { addSuffix: true })
    : 'Never'
  const isStale = sync?.lastSyncAt ? Date.now() / 1000 - sync.lastSyncAt > 8 * 60 * 60 : true
  const isRunning = sync?.status === 'running'
  const isError = sync?.status === 'error'

  return (
    <section>
      <div className="flex items-end justify-between mb-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Catalogue Health
        </h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => syncMutation.mutate()}
          disabled={isRunning || syncMutation.isPending}
        >
          {isRunning || syncMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {isRunning ? 'Sync running' : 'Sync now'}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          icon={<Package className="h-4 w-4" />}
          label="Products indexed"
          value={isLoading ? null : stats?.catalogue.productCount.toLocaleString() ?? '0'}
          sub={
            stats
              ? `${stats.catalogue.brandCount} brands · ${stats.catalogue.categoryCount} categories`
              : undefined
          }
        />
        <Stat
          icon={<Eye className="h-4 w-4" />}
          label="AI vision enrichment"
          value={isLoading ? null : `${stats?.catalogue.enrichmentPercent ?? 0}%`}
          sub={
            stats
              ? `${stats.catalogue.productsEnriched.toLocaleString()} of ${stats.catalogue.productCount.toLocaleString()}`
              : undefined
          }
          progress={stats?.catalogue.enrichmentPercent ?? 0}
        />
        <Stat
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Out of stock"
          value={isLoading ? null : stats?.catalogue.outOfStock.toLocaleString() ?? '0'}
          sub={
            stats && stats.catalogue.productCount > 0
              ? `${Math.round((stats.catalogue.outOfStock / stats.catalogue.productCount) * 100)}% of catalogue`
              : undefined
          }
        />
        <Stat
          icon={<Clock className="h-4 w-4" />}
          label="Last sync"
          value={isLoading ? null : lastSync}
          sub={
            isError
              ? 'Last sync failed'
              : isRunning
                ? sync?.message ?? 'Running…'
                : isStale
                  ? 'Stale — consider syncing'
                  : 'Up to date'
          }
          tone={isError ? 'destructive' : isStale && !isRunning ? 'warning' : 'ok'}
          small
        />
      </div>

      {isRunning && sync && sync.total && sync.total > 0 && (
        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${Math.min(100, ((sync.progress ?? 0) / sync.total) * 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{sync.message}</p>
        </div>
      )}

      {isError && sync?.error && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Last sync failed</div>
            <div className="text-xs opacity-80 mt-0.5">{sync.error}</div>
          </div>
        </div>
      )}

      {syncMutation.error && (
        <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          Could not start sync: {(syncMutation.error as Error).message}
        </div>
      )}
    </section>
  )
}

function DemandSection({ stats, isLoading }: { stats?: HomeStats; isLoading: boolean }) {
  const d = stats?.demand
  const todayTrend = d ? pctChange(d.searchesToday, d.searchesYesterday) : null
  const weekTrend = d ? pctChange(d.searchesWeek, d.searchesPriorWeek) : null
  const zeroTrend = d ? pctChange(d.zeroResultRate7d, d.zeroResultRatePrior) : null
  const ctrTrend = d ? pctChange(d.clickThroughRate7d, d.clickThroughRatePrior) : null

  return (
    <section>
      <div className="flex items-end justify-between mb-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Demand
        </h2>
        <Link to="/dashboard/insights" className="text-xs text-primary hover:underline">
          Full insights →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          icon={<Search className="h-4 w-4" />}
          label="Searches today"
          value={isLoading ? null : d?.searchesToday.toLocaleString() ?? '0'}
          trend={todayTrend}
          trendLabel="vs yesterday"
        />
        <Stat
          icon={<Search className="h-4 w-4" />}
          label="Searches (7d)"
          value={isLoading ? null : d?.searchesWeek.toLocaleString() ?? '0'}
          trend={weekTrend}
          trendLabel="vs prior 7d"
        />
        <Stat
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Zero-result rate"
          value={isLoading ? null : `${d?.zeroResultRate7d ?? 0}%`}
          trend={zeroTrend}
          trendLabel="vs prior 7d"
          trendInvert
        />
        <Stat
          icon={<Zap className="h-4 w-4" />}
          label="Click-through rate"
          value={isLoading ? null : `${d?.clickThroughRate7d ?? 0}%`}
          trend={ctrTrend}
          trendLabel="vs prior 7d"
        />
      </div>

      {d && d.uniqueSessions7d > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          <Users className="inline h-3 w-3 mr-1" />
          {d.uniqueSessions7d.toLocaleString()} unique visitors searched this week
        </p>
      )}
    </section>
  )
}

function AttentionSection({ stats, isLoading }: { stats?: HomeStats; isLoading: boolean }) {
  const zero = stats?.attention.zeroResults ?? []
  const struggling = stats?.attention.strugglingQueries ?? []
  const hasAnything = zero.length > 0 || struggling.length > 0

  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
        Needs Your Attention
      </h2>

      {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : !hasAnything ? (
        <div className="rounded-lg border bg-emerald-500/5 p-6 flex items-center gap-3 text-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <div>
            <div className="font-medium">All clear this week</div>
            <div className="text-muted-foreground">
              No zero-results and no struggling queries. Keep an eye on /insights for emerging patterns.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Zero-results */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Gaps in the catalogue
              </div>
              <Link
                to="/dashboard/insights"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                View all →
              </Link>
            </div>
            {zero.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No zero-result searches this week.</p>
            ) : (
              <ul className="space-y-2">
                {zero.map((r) => (
                  <li
                    key={r.query}
                    className="flex items-center justify-between gap-3 rounded-md bg-amber-500/5 border border-amber-500/10 px-2.5 py-1.5"
                  >
                    <span className="truncate text-sm font-medium">"{r.query}"</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {r.count} search{r.count === 1 ? '' : 'es'}
                      </span>
                      <Link
                        to={`/dashboard/insights#zero-results`}
                        className="text-xs text-primary hover:underline"
                      >
                        fix
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Add a synonym, redirect, or product for any term customers can't find.
            </p>
          </div>

          {/* Struggling queries — volume but no clicks */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <TrendingDown className="h-4 w-4 text-amber-500" />
                Low-CTR searches
              </div>
              <Link
                to="/dashboard/insights"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                View all →
              </Link>
            </div>
            {struggling.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                Nothing underperforming right now.
              </p>
            ) : (
              <ul className="space-y-2">
                {struggling.map((r) => (
                  <li
                    key={r.query}
                    className="flex items-center justify-between gap-3 rounded-md bg-muted/40 border px-2.5 py-1.5"
                  >
                    <span className="truncate text-sm font-medium">"{r.query}"</span>
                    <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground tabular-nums">
                      <span>{r.searches} searches</span>
                      <span>·</span>
                      <span
                        className={
                          r.ctr === 0
                            ? 'text-amber-600 dark:text-amber-400 font-medium'
                            : ''
                        }
                      >
                        {r.ctr}% CTR
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Queries with results but no clicks — wrong products ranking? Consider a searchandising rule.
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

function TrendingSection({ stats, isLoading }: { stats?: HomeStats; isLoading: boolean }) {
  const topQueries = stats?.trending.topQueries ?? []
  const topProducts = stats?.trending.topClickedProducts ?? []
  const recent = stats?.recent ?? []

  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
        What's Trending
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top queries */}
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TrendingUp className="h-4 w-4" />
              Top searches this week
            </div>
          </div>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : topQueries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No searches yet this week.</p>
          ) : (
            <ul className="space-y-1.5">
              {topQueries.map((q) => (
                <li key={q.query} className="flex items-center justify-between gap-3 text-sm">
                  <Link
                    to={`/dashboard/search-preview?q=${encodeURIComponent(q.query)}`}
                    className="truncate hover:text-primary"
                  >
                    {q.query}
                  </Link>
                  <div className="flex items-center gap-2 shrink-0 text-xs tabular-nums">
                    <span className="text-muted-foreground">{q.count}</span>
                    <TrendBadge trend={q.trend} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top clicked products */}
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Tag className="h-4 w-4" />
              Top clicked products
            </div>
          </div>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : topProducts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No click data yet this week.</p>
          ) : (
            <ul className="space-y-1.5">
              {topProducts.map((p) => (
                <li key={p.productId} className="flex items-center gap-2 text-sm">
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt=""
                      className="h-8 w-8 rounded object-cover shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{p.title}</div>
                    {p.brand && <div className="text-xs text-muted-foreground">{p.brand}</div>}
                  </div>
                  <span className="text-xs font-medium tabular-nums shrink-0">
                    {p.clicks}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity line */}
      {recent.length > 0 && (
        <div className="mt-4 rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <Clock className="h-4 w-4" />
            Most recent searches
          </div>
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
            {recent.map((r, i) => (
              <li
                key={`${r.query}-${i}`}
                className="flex items-baseline justify-between gap-2 text-sm"
              >
                <Link
                  to={`/dashboard/search-preview?q=${encodeURIComponent(r.query)}`}
                  className="truncate hover:text-primary"
                >
                  {r.query}
                </Link>
                <span className="text-xs text-muted-foreground shrink-0">
                  {r.resultCount === 0 ? (
                    <span className="text-amber-600 dark:text-amber-400">0 results</span>
                  ) : (
                    `${r.resultCount} results`
                  )}{' '}
                  · {formatDistanceToNow(r.timestamp * 1000, { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────
// Bits
// ─────────────────────────────────────────────

function Stat({
  icon,
  label,
  value,
  sub,
  trend,
  trendLabel,
  trendInvert,
  tone,
  small,
  progress,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
  sub?: string
  trend?: number | null
  trendLabel?: string
  trendInvert?: boolean
  tone?: 'ok' | 'warning' | 'destructive'
  small?: boolean
  progress?: number
}) {
  const toneClass =
    tone === 'destructive'
      ? 'border-destructive/30 bg-destructive/5'
      : tone === 'warning'
        ? 'border-amber-500/30 bg-amber-500/5'
        : 'border-border'

  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-7 w-20 mt-1.5" />
      ) : (
        <div className={small ? 'text-sm font-semibold mt-1.5' : 'text-2xl font-semibold mt-1'}>
          {value}
        </div>
      )}
      {typeof progress === 'number' && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {(sub || trend !== undefined) && (
        <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          {sub ? <span className="truncate">{sub}</span> : <span />}
          {trend !== undefined && trend !== null && (
            <TrendBadge trend={trend} invert={trendInvert} />
          )}
        </div>
      )}
      {trendLabel && trend !== null && trend !== undefined && (
        <div className="text-[10px] text-muted-foreground/80 mt-0.5">{trendLabel}</div>
      )}
    </div>
  )
}

function TrendBadge({ trend, invert = false }: { trend: number | null; invert?: boolean }) {
  if (trend === null) {
    return <span className="inline-flex text-[10px] uppercase text-muted-foreground">new</span>
  }
  if (trend === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
      </span>
    )
  }
  const isGood = invert ? trend < 0 : trend > 0
  const Icon = trend > 0 ? TrendingUp : TrendingDown
  const colour = isGood
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-amber-600 dark:text-amber-400'
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${colour}`}>
      <Icon className="h-3 w-3" />
      {trend > 0 ? '+' : ''}
      {trend}%
    </span>
  )
}

function pctChange(current: number, prior: number): number | null {
  if (prior === 0) return null
  return Math.round(((current - prior) / prior) * 100)
}
